/**
 * O TRECHO QUE O CLIENTE COLA NA LANDING PAGE DELE.
 *
 * Este arquivo é o coração do valor da tela de redirecionadores, e vale explicar por quê.
 *
 * O funil real é `anúncio Meta → LP do cliente → r.…/slug → WhatsApp`. Os parâmetros
 * (`utm_*`, `fbclid`, as macros da Meta) estão na URL **da LP**, não no `href` do botão. Quem
 * escreve o óbvio —
 *
 * ```html
 * <a href="https://r.mgdleads.tech/abc">Entrar no grupo</a>
 * ```
 *
 * — manda o clique para nós **pelado**: o redirect funciona, o grupo é alocado, a pessoa
 * entra, e a ligação com o anúncio morre sem um único erro em lugar nenhum (ADR-0013). Este
 * trecho é o que fecha esse buraco, e por isso ele é **texto literal**: é copiado e colado
 * numa página que não é nossa, onde nada nosso roda para corrigir depois.
 *
 * ## As quatro decisões do trecho
 *
 *  1. **`data-mgdlead`, não `id`.** A versão com `getElementById` cuida de UM botão. LP de
 *     verdade tem o botão no topo, no meio e no rodapé — e os outros dois ficariam sem
 *     repasse, em silêncio, que é exatamente a falha que este trecho existe para evitar.
 *  2. **`searchParams.set`, não `href + location.search`.** Concatenar é o que quebra quando
 *     o script roda duas vezes (construtor de página re-injeta bloco de código o tempo todo):
 *     o `href` viraria `?utm_source=ig?utm_source=ig`. `set` é idempotente por definição —
 *     rodar dez vezes dá o mesmo resultado de rodar uma.
 *  3. **Espera o DOM quando precisa.** Elementor, Webflow e Framer costumam colar código
 *     personalizado no `<head>`, onde o botão ainda não existe. Sem a guarda de
 *     `readyState`, o trecho certo, colado no lugar que o construtor oferece, não faria nada.
 *  4. **Sai calado quando não há query.** Uma LP aberta direto (sem `?`) não é instalação
 *     errada, é uma visita sem origem — e mexer no `href` à toa só criaria risco.
 *
 * O teste (`test/redirector-install.test.ts`) **executa esta string** dentro do jsdom, contra
 * um `<a>` de verdade e uma `location` de verdade. Não é o teste de uma cópia da regra: é o
 * teste do texto que a tela entrega.
 */

/** O atributo que marca o botão. Aparece no trecho e no texto que o explica. */
export const SNIPPET_MARKER = 'data-mgdlead';

/**
 * Monta o trecho de instalação para um link já pronto.
 *
 * `shortUrl` entra inteiro (com `https://` e o slug) porque é assim que ele vai para o
 * `href` — montar a URL aqui dentro convidaria a tela a passar só o slug e a errar a base.
 */
export function buildInstallSnippet(shortUrl: string): string {
  return `<!-- 1. O botão da sua página. O ${SNIPPET_MARKER} é a marca que o script procura. -->
<a ${SNIPPET_MARKER} href="${shortUrl}">Entrar no grupo</a>

<!-- 2. Cole este trecho UMA vez na página (topo ou rodapé, tanto faz). -->
<script>
(function () {
  function repassar() {
    var query = window.location.search;
    if (query.length < 2) return;
    var daPagina = new URLSearchParams(query);
    var links = document.querySelectorAll('a[${SNIPPET_MARKER}]');
    for (var i = 0; i < links.length; i++) {
      var destino = new URL(links[i].href);
      daPagina.forEach(function (valor, chave) {
        destino.searchParams.set(chave, valor);
      });
      links[i].href = destino.toString();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repassar);
  } else {
    repassar();
  }
})();
</script>`;
}

/**
 * A frase que explica POR QUE o trecho existe.
 *
 * Fica aqui, e não dentro do JSX, porque é a mesma frase na gaveta de instalação e no aviso
 * de diagnóstico — e porque um texto que muda de lugar em lugar vira dois textos que se
 * contradizem.
 */
export const POR_QUE_O_TRECHO =
  'Os parâmetros do anúncio (utm_*, fbclid) ficam na URL da sua página, não no endereço do botão. ' +
  'Sem este trecho, o clique chega até nós sem nenhum sinal de origem: o redirect funciona, a pessoa ' +
  'entra no grupo — e a entrada não pode ser creditada a nenhuma campanha.';
